import { objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, typeof as _typeof, extends as _extends, defineProperty as _defineProperty } from './_virtual/_rollupPluginBabelHelpers.js';
import { ThemeUIProvider, utils, formikField, useSafeThemeUI, isFlatDesign, useForkRef, useSize, useTimeout, useDrawerContext, scrollbarWidth, useCallbackRef, calculatedOffsets, useEffectOnce, StandardCheckListItem, StandardListItem, getPopoverAndAnchorStatus, PopoverAdorner, List, Button, Box, Text, scrollbar, INPUT_HOVER_PSEUDO_FIELD, INPUT_FOCUS_PSEUDO_FIELD, FlexInline, Popover, Input, ListSubheader } from '@costar/theme-ui';
import { IconClose } from '@costar/theme-ui-icons/csg/IconClose';
import classNames from 'classnames';
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import isReact from 'react-is';
import { getAutocompleteUtilityClass } from './autocomplete-classes.esm.js';
import defaultRenderMultiSelectText from './components/default-render-multi-select-text.esm.js';
import defaultRenderTags from './components/default-render-tags.esm.js';
import defaultGetOptionLabel from './helpers/default-get-option-label.esm.js';
import useAutocomplete from './hooks/use-autocomplete.esm.js';
import AutocompleteTheme from './theme.esm.js';

var _excluded = ["openState"],
  _excluded2 = ["key"],
  _excluded3 = ["paneProps"],
  _excluded4 = ["ref", "sx"],
  _excluded5 = ["isInvalid"],
  _excluded6 = ["ref"];
// import { useWhatChanged } from '@simbathesailor/use-what-changed'

var defaultListProps = {
  disablePadding: true
};
var useUtilityClasses = function useUtilityClasses(ownerState) {
  var classes = ownerState.classes,
    disablePortal = ownerState.disablePortal,
    focused = ownerState.focused,
    fullWidth = ownerState.fullWidth,
    hasClearIcon = ownerState.hasClearIcon,
    hasPopupIcon = ownerState.hasPopupIcon,
    inputFocused = ownerState.inputFocused,
    popupOpen = ownerState.popupOpen,
    size = ownerState.size;
  var slots = {
    root: ['root', focused && 'focused', fullWidth && 'fullWidth', hasClearIcon && 'hasClearIcon', hasPopupIcon && 'hasPopupIcon'],
    inputRoot: ['inputRoot'],
    input: ['input', inputFocused && 'inputFocused'],
    tag: ['tag', typeof size === 'string' && "tagSize".concat(utils.capitalize(size))],
    endAdornment: ['endAdornment'],
    clearIndicator: ['clearIndicator'],
    popupIndicator: ['popupIndicator', popupOpen && 'popupIndicatorOpen'],
    popper: ['popper', disablePortal && 'popperDisablePortal'],
    paper: ['paper'],
    listbox: ['listbox'],
    loading: ['loading'],
    noOptions: ['noOptions'],
    option: ['option'],
    groupLabel: ['groupLabel'],
    groupUl: ['groupUl']
  };
  return utils.composeClasses(slots, getAutocompleteUtilityClass, classes);
};
function extractPopupContentSize(o) {
  var minWidth = utils.get(o, 'style.minWidth');
  var maxWidth = utils.get(o, 'style.maxWidth');
  return minWidth || maxWidth ? {
    style: _objectSpread2(_objectSpread2({}, minWidth ? {
      minWidth: minWidth
    } : {}), maxWidth ? {
      maxWidth: maxWidth
    } : {})
  } : {};
}

// use this typing technique to minimized issue with generic type propagation

var DefaultPopupIcon = function DefaultPopupIcon(props) {
  var openState = props.openState,
    rest = _objectWithoutProperties(props, _excluded);
  var iconAfter = useMemo(function () {
    return {
      icon: 'mui-arrowDropDown',
      shouldFlipIcon: true,
      toggleState: openState
    };
  }, [openState]);
  return /*#__PURE__*/React.createElement(Button, _extends({
    border: "unset",
    height: "unset",
    p: "unset",
    color: "neutral.darker",
    iconAfter: iconAfter
  }, rest));
};
function defaultPopupIcon(props) {
  return /*#__PURE__*/React.createElement(DefaultPopupIcon, props);
}
var DefaultClearIcon = /*#__PURE__*/React.createElement(IconClose, null);
var defaultInputProps = {
  lineHeight: 'unset' // clear standard line height the from the theme variant
};
var Group = function Group(props) {
  var className = props.className,
    group = props.group,
    children = props.children;
  // eslint-disable-next-line no-console
  // console.log(
  //   `----- render autocomplete option item group ${params.group} -----`
  // )
  return /*#__PURE__*/React.createElement(Box, {
    as: "li"
  }, /*#__PURE__*/React.createElement(Box, {
    as: "ul"
  }, /*#__PURE__*/React.createElement(ListSubheader, _extends({
    variant: "transferList.listSubheader",
    className: className,
    disableGutters: isReact.isElement(group)
  }, _objectSpread2({}, isReact.isElement(group) ? {} : {
    bg: 'neutral.light'
  })), group), children));
};
var MemoGroup = /*#__PURE__*/React.memo(Group);
var defaultRenderGroup = function defaultRenderGroup(params) {
  var key = params.key,
    rest = _objectWithoutProperties(params, _excluded2);
  return /*#__PURE__*/React.createElement(MemoGroup, _extends({
    key: "list-item-group-".concat(key)
  }, rest));
};
var defaultReadOnlyTag = {
  color: 'neutral',
  outline: false
};
var defaultListPropsForCheckList = {
  variant: 'checkbox'
};
var ExtendedAutocompleteInput = /*#__PURE__*/React.forwardRef(function ExtendedAutocompleteInput(props, ref) {
  return /*#__PURE__*/React.createElement(Input, _extends({
    ref: ref
  }, props));
});

// @ts-ignore
var AutocompletePrivate = /*#__PURE__*/React.forwardRef(function AutocompletePrivate(props, ref) {
  var _useDrawerContext, _rest$value$toString$, _rest$value;
  // eslint-disable-next-line no-console
  // console.log(
  //   `--- render @costar/theme-ui-autocomplete AutocompletePrivate ${props['automation-id']} -----`
  // )
  var theme = useSafeThemeUI(props);
  var isFlatDesign$1 = isFlatDesign(theme);
  var className = props.className,
    automationId = props['automation-id'],
    display = props.display,
    _props$clearIcon = props.clearIcon,
    clearIcon = _props$clearIcon === void 0 ? DefaultClearIcon : _props$clearIcon,
    _props$clearText = props.clearText,
    clearText = _props$clearText === void 0 ? 'Clear' : _props$clearText,
    _props$closeText = props.closeText,
    closeText = _props$closeText === void 0 ? 'Close' : _props$closeText,
    _props$componentsProp = props.componentsProps,
    componentsProps = _props$componentsProp === void 0 ? utils.EMPTY_OBJECT : _props$componentsProp,
    _props$disableClearab = props.disableClearable,
    disableClearable = _props$disableClearab === void 0 ? false : _props$disableClearab,
    disabled = props.disabled,
    _props$disablePortal = props.disablePortal,
    disablePortal = _props$disablePortal === void 0 ? false : _props$disablePortal,
    fixToInputWidth = props.fixToInputWidth,
    _fontSize = props.fontSize,
    _forcePopupIcon = props.forcePopupIcon,
    _props$freeSolo = props.freeSolo,
    freeSolo = _props$freeSolo === void 0 ? false : _props$freeSolo,
    _props$fullWidth = props.fullWidth,
    fullWidth = _props$fullWidth === void 0 ? false : _props$fullWidth,
    _props$fullWidthPopov = props.fullWidthPopover,
    fullWidthPopover = _props$fullWidthPopov === void 0 ? true : _props$fullWidthPopov,
    _props$getLimitTagsTe = props.getLimitTagsText,
    getLimitTagsText = _props$getLimitTagsTe === void 0 ? function (more) {
      return "+".concat(more);
    } : _props$getLimitTagsTe,
    _props$getOptionLabel = props.getOptionLabel,
    getOptionLabel = _props$getOptionLabel === void 0 ? defaultGetOptionLabel : _props$getOptionLabel,
    _getTagLabel = props.getTagLabel,
    groupBy = props.groupBy,
    hidableTags = props.hidableTags,
    hidePlaceholderProps = props.hidePlaceholder,
    isSelectAllOption = props.isSelectAllOption,
    isResetOption = props.isResetOption,
    limitTagsProps = props.limitTags,
    _props$ListboxCompone = props.ListboxComponent,
    ListboxComponent = _props$ListboxCompone === void 0 ? 'ul' : _props$ListboxCompone,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    _props$isInvalid = props.isInvalid,
    isInvalid = _props$isInvalid === void 0 ? false : _props$isInvalid,
    _props$loadingText = props.loadingText,
    loadingText = _props$loadingText === void 0 ? 'Loading…' : _props$loadingText,
    minimumInputLength = props.minimumInputLength,
    _props$multiple = props.multiple,
    multiple = _props$multiple === void 0 ? false : _props$multiple,
    _props$mode = props.mode,
    mode = _props$mode === void 0 ? 'autocomplete' : _props$mode,
    _props$noOptionsText = props.noOptionsText,
    noOptionsText = _props$noOptionsText === void 0 ? 'No result found' : _props$noOptionsText,
    _props$openText = props.openText,
    openText = _props$openText === void 0 ? 'Open' : _props$openText,
    options = props.options,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? 'Select' : _props$placeholder,
    _props$popupIcon = props.popupIcon,
    popupIcon = _props$popupIcon === void 0 ? defaultPopupIcon : _props$popupIcon,
    _props$readOnly = props.readOnly,
    readOnlyProps = _props$readOnly === void 0 ? false : _props$readOnly,
    renderGroupProp = props.renderGroup,
    renderOptionProp = props.renderOption,
    renderPlaceHolder = props.renderPlaceHolder,
    renderRow = props.renderRow,
    getOptionSecondaryText = props.renderSecondaryText,
    renderTagsProps = props.renderTags,
    size = props.size,
    variant = props.variant,
    _props$width = props.width,
    width = _props$width === void 0 ? '100%' : _props$width,
    maxWidth = props.maxWidth,
    isVirtualize = props.isVirtualize,
    nonListExtendedContentTop = props.nonListExtendedContentTop,
    nonListExtendedContent = props.nonListExtendedContent,
    extendedContentTop = props.extendedContentTop,
    extendedContent = props.extendedContent,
    hideAdorner = props.hideAdorner;
  var fontSize = _fontSize ? _fontSize : size;

  // set default value
  var forcePopupIcon = typeof _forcePopupIcon !== 'undefined' ? _forcePopupIcon : freeSolo ? 'auto' : true;
  var _componentsProps$clea = componentsProps.clearIndicator,
    clearIndicatorProps = _componentsProps$clea === void 0 ? utils.EMPTY_OBJECT : _componentsProps$clea,
    _componentsProps$popu = componentsProps.popupIndicator,
    popupIndicatorProps = _componentsProps$popu === void 0 ? utils.EMPTY_OBJECT : _componentsProps$popu,
    _componentsProps$root = componentsProps.rootInput,
    rootInput = _componentsProps$root === void 0 ? utils.EMPTY_OBJECT : _componentsProps$root,
    _componentsProps$inpu = componentsProps.input,
    _inputProps = _componentsProps$inpu === void 0 ? utils.EMPTY_OBJECT : _componentsProps$inpu,
    _componentsProps$pane = componentsProps.pane,
    paneProps = _componentsProps$pane === void 0 ? utils.EMPTY_OBJECT : _componentsProps$pane,
    _popoverProps = componentsProps.popover,
    _componentsProps$list = componentsProps.listbox,
    _listProps = _componentsProps$list === void 0 ? (mode === 'select' || mode === 'searchable-select') && multiple ? defaultListPropsForCheckList : utils.EMPTY_OBJECT : _componentsProps$list,
    _componentsProps$tag = componentsProps.tag,
    tagProps = _componentsProps$tag === void 0 ? readOnlyProps ? defaultReadOnlyTag : utils.EMPTY_OBJECT : _componentsProps$tag,
    _componentsProps$ador = componentsProps.adorner,
    adornerProps = _componentsProps$ador === void 0 ? utils.EMPTY_OBJECT : _componentsProps$ador;
  var inputProps = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, _inputProps), isInvalid ? {
      state: 'danger'
    } : {});
  }, [isInvalid, _inputProps]);
  var paneLayoutProps = _objectSpread2(_objectSpread2({}, utils.getLayout(paneProps)), utils.getLayout(paneProps.sx));
  if (utils.isNotEmptyObject(paneLayoutProps)) {
    // eslint-disable-next-line no-console
    console.error('Tui: Setting layout css to pane can break positioning calculation of popover. Use listbox props to manipulate min max hight if needed.');
  }
  var limitTags = (mode === 'select' || mode === 'searchable-select') && multiple ? 1 : limitTagsProps;
  var getTagLabel = _getTagLabel ? _getTagLabel : getOptionLabel;
  var renderTags = renderTagsProps ? renderTagsProps : (mode === 'select' || mode === 'searchable-select') && multiple ? defaultRenderMultiSelectText(options, isSelectAllOption, getTagLabel) : defaultRenderTags(getTagLabel, size, tagProps);
  var _useMemo = useMemo(function () {
      var _ref = _popoverProps || {
          children: null
        },
        paneProps = _ref.paneProps,
        popoverRest = _objectWithoutProperties(_ref, _excluded3);
      return {
        paneProps: paneProps,
        popoverRest: popoverRest
      };
    }, [_popoverProps]),
    popoverPaneProps = _useMemo.paneProps,
    popoverRest = _useMemo.popoverRest;
  var _useMemo2 = useMemo(function () {
      var _popoverPaneProps$pan = _objectSpread2(_objectSpread2({}, popoverPaneProps), paneProps),
        ref = _popoverPaneProps$pan.ref,
        sx = _popoverPaneProps$pan.sx,
        restPaneProps = _objectWithoutProperties(_popoverPaneProps$pan, _excluded4);
      return {
        ref: ref,
        sx: sx,
        restPaneProps: restPaneProps
      };
    }, [popoverPaneProps, paneProps]),
    panePropsRef = _useMemo2.ref,
    _useMemo2$sx = _useMemo2.sx,
    sxPaneProp = _useMemo2$sx === void 0 ? utils.EMPTY_OBJECT : _useMemo2$sx,
    restPaneProps = _useMemo2.restPaneProps;
  var _useAutocomplete = useAutocomplete(_objectSpread2({
      id: props.id || 'autocomplete'
    }, props)),
    identifier = _useAutocomplete.id,
    getRootProps = _useAutocomplete.getRootProps,
    getInputProps = _useAutocomplete.getInputProps,
    getOptionProps = _useAutocomplete.getOptionProps,
    getListboxProps = _useAutocomplete.getListboxProps,
    getPopupIndicatorProps = _useAutocomplete.getPopupIndicatorProps,
    getClearProps = _useAutocomplete.getClearProps,
    getTagProps = _useAutocomplete.getTagProps,
    value = _useAutocomplete.value,
    dirty = _useAutocomplete.dirty,
    popupOpen = _useAutocomplete.popupOpen,
    focused = _useAutocomplete.focused,
    focusedTag = _useAutocomplete.focusedTag,
    anchorEl = _useAutocomplete.anchorEl,
    listboxRef = _useAutocomplete.listboxRef,
    inputValue = _useAutocomplete.inputValue,
    inputValueRef = _useAutocomplete.inputValueRef,
    groupedOptions = _useAutocomplete.groupedOptions,
    isMultiSelect = _useAutocomplete.isMultiSelect,
    openOnMouseDown = _useAutocomplete.openOnMouseDown,
    handleBlur = _useAutocomplete.handleBlur,
    handlePopupExited = _useAutocomplete.handlePopupExited,
    handlePopupEntering = _useAutocomplete.handlePopupEntering,
    isMaxTagsSelected = _useAutocomplete.isMaxTagsSelected;
  var hidePlaceholder = hidePlaceholderProps || mode === 'select' || isMaxTagsSelected || readOnlyProps;
  var hasClearIcon = !disableClearable && !disabled && dirty && !readOnlyProps;
  var hasPopupIcon = (!freeSolo || forcePopupIcon === true) && forcePopupIcon !== false && !(hasClearIcon && forcePopupIcon === 'auto');
  var ownerState = _objectSpread2(_objectSpread2({}, props), {}, {
    disablePortal: disablePortal,
    focused: focused,
    fullWidth: fullWidth,
    hasClearIcon: hasClearIcon,
    hasPopupIcon: hasPopupIcon,
    inputFocused: focusedTag === -1,
    popupOpen: popupOpen,
    size: size
  });
  var classes = useUtilityClasses(ownerState);
  var timerRef = useRef();
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    refreshToken = _useState2[0],
    setRefreshToken = _useState2[1];
  var paneRef = useRef(null);
  var _useInView = useInView({
      threshold: 0,
      initialInView: true
    }),
    inViewRef = _useInView.ref,
    inView = _useInView.inView;
  var _inputRef = useRef(null);
  var handelPaneRef = useForkRef(paneRef, panePropsRef);
  var mountRef = useRef(false);
  var _useState3 = useState(defaultListProps),
    _useState4 = _slicedToArray(_useState3, 2),
    listProps = _useState4[0],
    setListProps = _useState4[1];
  var inputSize = useSize(_inputRef);
  var delayExecute = useTimeout(150);
  var _ref2 = (_useDrawerContext = useDrawerContext()) !== null && _useDrawerContext !== void 0 ? _useDrawerContext : {},
    onEnteredObserver = _ref2.onEnteredObserver;
  var getScrollBarWidth = useCallback(function () {
    return paneRef.current && listboxRef.current && (listboxRef.current.getBoundingClientRect().height > paneRef.current.getBoundingClientRect().height || paneRef.current.scrollHeight > paneRef.current.clientHeight) ? scrollbarWidth // we are using theme-ui webkit definition scroll bar
    : 0;
  }, [listboxRef]);
  var updateListWidth = useCallback(function (prevState) {
    if (anchorEl) {
      var refRect = anchorEl.getBoundingClientRect();
      var newWidth = refRect.width - 2 - getScrollBarWidth();
      return _objectSpread2(_objectSpread2({}, prevState), {}, {
        style: _objectSpread2(_objectSpread2(_objectSpread2({}, prevState.style), fullWidthPopover ? {
          minWidth: newWidth
        } : {}), fixToInputWidth ? {
          maxWidth: newWidth
        } : {})
      });
    }
    return prevState;
  }, [anchorEl, fullWidthPopover, fixToInputWidth, getScrollBarWidth]);
  useEffect(function () {
    var handelOnEntered = function handelOnEntered() {
      delayExecute(function () {
        return setListProps(function (state) {
          return updateListWidth(state);
        });
      });
    };
    onEnteredObserver === null || onEnteredObserver === void 0 ? void 0 : onEnteredObserver.attach(handelOnEntered);
    return function () {
      return onEnteredObserver === null || onEnteredObserver === void 0 ? void 0 : onEnteredObserver.detach(handelOnEntered);
    };
  }, [delayExecute, onEnteredObserver, updateListWidth]);
  useEffect(function () {
    if (!utils.isEmptyObject(_listProps)) {
      setListProps(function (state) {
        return _objectSpread2(_objectSpread2({}, state), _listProps);
      });
    }
  }, [_listProps]);
  useEffect(function () {
    delayExecute(function () {
      return setListProps(function (state) {
        return updateListWidth(state);
      });
    });
  }, [inputValue, updateListWidth, delayExecute]);
  var handleClose = useCallbackRef(handleBlur);
  var handleOnEntered = useCallback(function (node) {
    // @ts-ignore
    handelPaneRef(node);
    setListProps(function (state) {
      return updateListWidth(state);
    });
    timerRef.current = setTimeout(function () {
      // eslint-disable-next-line no-console
      // console.log(`----- autocomplete handleOnEntered invoking refresh -----`)
      setRefreshToken(function (prev) {
        return ++prev;
      });
    }, 100);
  }, [handelPaneRef, updateListWidth]);
  var handleOnExited = useCallback(function () {
    handlePopupExited();
  }, [handlePopupExited]);
  var handlePopoverReposition = useCallback(function (refreshToken) {
    // eslint-disable-next-line no-console
    // console.log(
    //   `----- autocomplete handlePopoverReposition refreshToken: ${refreshToken} -----`
    // )
    paneRef.current && setRefreshToken(function (prev) {
      // provide circuit breaker for a race condition
      return refreshToken === prev ? prev : ++prev;
    });
  }, []);
  var panePropsCss = useMemo(function () {
    if (size) {
      var offset = utils.getStepSize(size, 2);
      return {
        __css: {
          maxWidth: calculatedOffsets(offset, theme),
          maxHeight: calculatedOffsets(offset, theme)
        }
      };
    }
    return {};
  }, [size, theme]);
  useEffectOnce(function () {
    mountRef.current = true;
  });
  useEffect(function () {
    return function () {
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, []);
  var startAdornment;
  var hasSelectedValues = Array.isArray(value) && value.length > 0;
  if (multiple && hasSelectedValues) {
    var getCustomizedTagProps = function getCustomizedTagProps(_ref3) {
      var index = _ref3.index,
        option = _ref3.option;
      var _getTagProps = getTagProps({
          index: index,
          option: option
        }),
        isInvalid = _getTagProps.isInvalid,
        rest = _objectWithoutProperties(_getTagProps, _excluded5);
      return _objectSpread2({
        className: classNames(classes.tag, {
          'is-last-tag': value.length === index + 1,
          'is-invalid': isInvalid
        }),
        disabled: disabled
      }, rest);
    };
    var node = renderTags(value, getCustomizedTagProps);
    // extract children nodes if it's wrapped by fragments
    // this step is necessary for processing of the limitTag
    startAdornment = _typeof(node) === 'object' && node !== null && Array.isArray(utils.get(node, 'props.children')) ? utils.get(node, 'props.children') : node;
  }
  if (limitTags && limitTags > -1 && Array.isArray(startAdornment)) {
    var more = startAdornment.length - limitTags;
    if ((!focused || mode === 'select' || mode === 'searchable-select') && more > 0) {
      startAdornment = startAdornment.splice(0, limitTags);
      startAdornment.push(/*#__PURE__*/React.createElement("span", {
        className: classes.tag,
        key: startAdornment.length
      }, getLimitTagsText(more)));
    }
  }
  var renderGroup = renderGroupProp || defaultRenderGroup;
  var renderOption = useCallback(function (props2, option, state) {
    if (renderOptionProp) {
      return renderOptionProp(props2, option, state);
    } else {
      // default renderOptions
      return (mode === 'select' || mode === 'searchable-select') && multiple ? /*#__PURE__*/React.createElement(StandardCheckListItem, _extends({
        variant: "primary"
      }, props2, {
        checked: state.selected,
        label: getOptionLabel(option)
      })) : /*#__PURE__*/React.createElement(StandardListItem, _extends({
        divider: !!getOptionSecondaryText
      }, props2, {
        primary: getOptionLabel(option),
        secondary: getOptionSecondaryText && getOptionSecondaryText(option)
      }));
    }
  }, [getOptionLabel, getOptionSecondaryText, mode, multiple, renderOptionProp]);

  // useWhatChanged(
  //   [classes.option, getOptionProps, inputValue, renderOption],
  //   'classes.option, getOptionProps, inputValue, renderOption'
  // )

  var renderListOption = useCallback(function (option, index, isResetOption) {
    var optionProps = getOptionProps({
      option: option,
      index: index
    });
    var isResetBool = isResetOption ? isResetOption(option) : false;
    return renderOption(_objectSpread2(_objectSpread2({}, optionProps), {}, {
      className: classNames(classes.option, {
        'is-reset-option': isResetBool
      })
    }), option, {
      selected: !!optionProps['aria-selected'],
      inputValue: inputValue
    });
  }, [classes.option, getOptionProps, inputValue, renderOption]);
  var _getInputProps = getInputProps(),
    inputRef = _getInputProps.ref,
    rest = _objectWithoutProperties(_getInputProps, _excluded6);
  var id = popupOpen ? identifier : undefined;
  var openPopup = !disabled && popupOpen && (
  // hide popup if minimumInputLength threshold has not been reached and there is no custom popOver placeholder UI
  !openOnMouseDown && minimumInputLength && !renderPlaceHolder ? ((_rest$value$toString$ = (_rest$value = rest.value) === null || _rest$value === void 0 ? void 0 : _rest$value.toString().trim().length) !== null && _rest$value$toString$ !== void 0 ? _rest$value$toString$ : 0) >= minimumInputLength : true);
  var _getPopoverAndAnchorS = getPopoverAndAnchorStatus(paneRef.current, anchorEl),
    isOverlapPopover = _getPopoverAndAnchorS.isPopoverOverlappingAnchor,
    isTopOpeningPopover = _getPopoverAndAnchorS.isPopoverOpeningUp;

  // console.log('Auto Complete Render', {
  //   isOverlapPopover,
  //   isTopOpeningPopover,
  //
  //   left: paneRef?.current?.getBoundingClientRect().left,
  //   top: paneRef?.current?.getBoundingClientRect().top,
  //   right: paneRef?.current?.getBoundingClientRect().right,
  //   bottom: paneRef?.current?.getBoundingClientRect().bottom
  // })

  var isMultiAutocomplete = mode !== 'select' && multiple;
  var renderAdornerComponent = useCallback(function () {
    return !isFlatDesign$1 && !hideAdorner && !isOverlapPopover ? /*#__PURE__*/React.createElement(PopoverAdorner, _extends({
      variant: variant,
      anchorRect: anchorEl ? anchorEl.getBoundingClientRect() : null,
      dropdownRect: paneRef.current ? paneRef.current.getBoundingClientRect() : null,
      isTop: isTopOpeningPopover,
      borderShadowEclipseThickness: multiple ? 5 : 6 // adjust to not overlap the tags for multiple
    }, adornerProps)) : null;
  }, [hideAdorner, isOverlapPopover, variant, anchorEl, isTopOpeningPopover, isFlatDesign$1, multiple, adornerProps]);
  var handleOnOptionsOnClick = useCallback(function (event) {
    // Prevent input blur when interacting with the "no options" content
    event.preventDefault();
  }, []);
  var rootProps = useMemo(function () {
    var _theme$transitions, _sx;
    return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
      display: display,
      className: classNames(classes.root, {
        'show-focus': openPopup,
        'select-mode': mode === 'select' && !readOnlyProps
      }, className),
      'automation-id': automationId
    }, isMultiAutocomplete ? {
      sx: (_sx = {
        lineHeight: 'body'
      }, _defineProperty(_sx, "& .".concat(classes.clearIndicator), {
        opacity: 0,
        transition: (_theme$transitions = theme.transitions) === null || _theme$transitions === void 0 ? void 0 : _theme$transitions.create('opacity')
      }), _defineProperty(_sx, '&:hover', _defineProperty({}, "& .".concat(classes.clearIndicator), {
        opacity: 1
      })), _sx)
    } : mode === 'select' ? {
      gap: 'xxs',
      flexWrap: 'nowrap'
    } : {}), isMultiAutocomplete && hasSelectedValues ? _objectSpread2({
      pr: 'xxl',
      pl: 'xs-2',
      alignItems: 'center',
      columnGap: 'xs-2',
      rowGap: '0.125em'
    }, isTopOpeningPopover ? {
      emPaddingTop: 'xs-2'
    } : {
      emPaddingY: 'xs-3'
    }) : {}), isMultiSelect && hasSelectedValues ? {
      pl: 'xs'
    } : {}), getRootProps(rootInput));
  }, [display, classes.root, classes.clearIndicator, openPopup, mode, readOnlyProps, className, automationId, isMultiAutocomplete, theme.transitions, hasSelectedValues, isTopOpeningPopover, isMultiSelect, getRootProps, rootInput]);

  // useWhatChanged(
  //   [
  //     classes.groupLabel,
  //     groupBy,
  //     groupedOptions,
  //     openPopup,
  //     renderGroup,
  //     renderListOption
  //   ],
  //   'classes.groupLabel, groupBy, groupedOptions, openPopup, renderGroup, renderListOption'
  // )

  var listContent = useMemo(function () {
    return openPopup && groupedOptions.length > 0 ? groupedOptions.map(function (option, index) {
      if (groupBy) {
        return renderGroup({
          inputValue: (inputValueRef === null || inputValueRef === void 0 ? void 0 : inputValueRef.current) || '',
          className: classes.groupLabel,
          key: option.key,
          group: option.group,
          children: option.options.map(function (option2, index2) {
            return renderListOption(option2, option.index + index2, isResetOption);
          })
        });
      }
      return renderListOption(option, index, isResetOption);
    }) : null;
  }, [classes.groupLabel, groupBy, groupedOptions, inputValueRef, isResetOption, openPopup, renderGroup, renderListOption]);

  // useWhatChanged(
  //   [
  //     ListboxComponent,
  //     classes.listbox,
  //     extendedContent,
  //     extendedContentTop,
  //     getListboxProps,
  //     groupedOptions.length,
  //     listContent,
  //     listProps,
  //     openPopup,
  //     renderRow
  //   ],
  //   'ListboxComponent, classes.listbox, extendedContent, extendedContentTop, getListboxProps, groupedOptions.length, listContent, listProps, openPopup,  renderRow'
  // )

  var popupList = useMemo(function () {
    var _objectSpread2$1;
    var _listProps = _objectSpread2(_objectSpread2(_objectSpread2((_objectSpread2$1 = {}, _defineProperty(_objectSpread2$1, 'as', ListboxComponent), _defineProperty(_objectSpread2$1, "disablePadding", true), _defineProperty(_objectSpread2$1, "className", classes.listbox), _defineProperty(_objectSpread2$1, "maxHeight", '40vh'), _objectSpread2$1), renderRow ? {
      renderRow: renderRow
    } : {}), {}, {
      overflowY: 'auto',
      overflowX: 'hidden'
    }, getListboxProps()), listProps);
    return openPopup && groupedOptions.length > 0 ?
    // virtualize autocomplete can not support null/undefined children and does not support
    // extendedContent, we only want to add extendedContent into children of the list only if it exist
    // and should not use VirtualizeAutocomplete when extendedContent is require.
    extendedContent || extendedContentTop ? extendedContent && extendedContentTop ?
    /*#__PURE__*/
    // include both top and bottom extended elements
    React.createElement(List, _listProps, extendedContentTop, listContent, extendedContent) : extendedContent ?
    /*#__PURE__*/
    // include only bottom extended elements
    React.createElement(List, _listProps, listContent, extendedContent) :
    /*#__PURE__*/
    // include only top extended elements
    React.createElement(List, _listProps, extendedContentTop, listContent) :
    /*#__PURE__*/
    // no extended elements
    React.createElement(List, _listProps, listContent) : null;
  }, [ListboxComponent, classes.listbox, extendedContent, extendedContentTop, getListboxProps, groupedOptions.length, listContent, listProps, openPopup, renderRow]);

  // useWhatChanged(
  //   [
  //     classes.clearIndicator,
  //     classes.endAdornment,
  //     classes.popupIndicator,
  //     clearIcon,
  //     clearIndicatorProps,
  //     clearText,
  //     closeText,
  //     disabled,
  //     fontSize,
  //     getClearProps,
  //     getPopupIndicatorProps,
  //     hasClearIcon,
  //     hasPopupIcon,
  //     isMaxTagsSelected,
  //     openPopup,
  //     openText,
  //     popupIcon,
  //     popupIndicatorProps,
  //     readOnlyProps
  //   ],
  //   'classes.clearIndicator, classes.endAdornment, classes.popupIndicator, clearIcon, clearIndicatorProps, clearText, closeText, disabled, fontSize, getClearProps, getPopupIndicatorProps, hasClearIcon, hasPopupIcon, isMaxTagsSelected, openPopup, openText, popupIcon, popupIndicatorProps, readOnlyProps'
  // )

  var _useMemo3 = useMemo(function () {
      return {
        afterProps: {
          className: classes.endAdornment,
          fontSize: fontSize
        },
        after: /*#__PURE__*/React.createElement(React.Fragment, null, hasClearIcon ? /*#__PURE__*/React.createElement(Button, _extends({
          variant: "icon.clear",
          "tab-index": "-1"
        }, getClearProps(), {
          "aria-label": clearText,
          icon: clearIcon,
          title: clearText,
          fontSize: fontSize,
          p: "xs-3",
          className: classNames(classes.clearIndicator, clearIndicatorProps.className)
        }, utils.omit(clearIndicatorProps, ['className']))) : null, hasPopupIcon ? typeof popupIcon === 'function' ? popupIcon(_objectSpread2(_objectSpread2({
          variant: 'icon',
          tabIndex: -1
        }, getPopupIndicatorProps()), {}, {
          disabled: disabled || readOnlyProps || isMaxTagsSelected,
          fontSize: fontSize,
          'aria-label': openPopup ? closeText : openText,
          title: openPopup ? closeText : openText,
          className: classNames(classes.popupIndicator),
          openState: openPopup
        }, popupIndicatorProps)) : popupIcon : null)
      };
    }, [classes.clearIndicator, classes.endAdornment, classes.popupIndicator, clearIcon, clearIndicatorProps, clearText, closeText, disabled, fontSize, getClearProps, getPopupIndicatorProps, hasClearIcon, hasPopupIcon, isMaxTagsSelected, openPopup, openText, popupIcon, popupIndicatorProps, readOnlyProps]),
    after = _useMemo3.after,
    afterProps = _useMemo3.afterProps;
  // useWhatChanged(
  //   [
  //     classes.loading,
  //     classes.noOptions,
  //     groupedOptions.length,
  //     handleOnOptionsOnClick,
  //     isVirtualize,
  //     listProps,
  //     loading,
  //     loadingText,
  //     minimumInputLength,
  //     noOptionsText,
  //     nonListExtendedContent,
  //     nonListExtendedContentTop,
  //     openPopup,
  //     popupList,
  //     renderPlaceHolder,
  //     rest.value
  //   ],
  //   'classes.loading, classes.noOptions, groupedOptions.length, handleOnOptionsOnClick, isVirtualize, listProps, loading, loadingText,  minimumInputLength, noOptionsText, nonListExtendedContent, nonListExtendedContentTop, openPopup, popupList, renderPlaceHolder, rest.value'
  // )
  var popoverContent = useMemo(function () {
    return /*#__PURE__*/React.createElement(React.Fragment, null, openPopup && loading && groupedOptions.length === 0 ? typeof loadingText === 'function' ? loadingText(_objectSpread2({
      className: classes.loading
    }, extractPopupContentSize(listProps))) : /*#__PURE__*/React.createElement(Box, _extends({
      className: classes.loading,
      px: "md",
      py: "sm",
      role: "presentation",
      color: "text"
    }, extractPopupContentSize(listProps)), /*#__PURE__*/React.createElement(Text, null, loadingText)) : null, openPopup && groupedOptions.length === 0 && !loading ? renderPlaceHolder && "".concat(rest.value).trim().length < (minimumInputLength || 0) ? renderPlaceHolder(extractPopupContentSize(listProps)) : typeof noOptionsText === 'function' ? noOptionsText(_objectSpread2({
      className: classes.noOptions
    }, extractPopupContentSize(listProps))) : /*#__PURE__*/React.createElement(Box, _extends({
      className: classes.noOptions,
      px: "md",
      py: "sm",
      role: "presentation",
      color: "text"
    }, extractPopupContentSize(listProps), {
      onMouseDown: handleOnOptionsOnClick
    }), noOptionsText) : null, openPopup && groupedOptions.length > 0 ?
    // virtualize autocomplete can not support null/undefined children and does not support
    // extendedContent, we only want to add extendedContent into children of the list only if it exist
    // and should not use VirtualizeAutocomplete when extendedContent is require.
    isVirtualize ? popupList : /*#__PURE__*/React.createElement(React.Fragment, null, nonListExtendedContentTop, popupList, nonListExtendedContent) : null);
  }, [classes.loading, classes.noOptions, groupedOptions.length, handleOnOptionsOnClick, isVirtualize, listProps, loading, loadingText, minimumInputLength, noOptionsText, nonListExtendedContent, nonListExtendedContentTop, openPopup, popupList, renderPlaceHolder, rest.value]);
  // useWhatChanged(
  //   [
  //     fontSize,
  //     inputProps.state,
  //     isOverlapPopover,
  //     mode,
  //     panePropsCss,
  //     popoverRest,
  //     restPaneProps,
  //     sxPaneProp
  //   ],
  //   'fontSize, inputProps.state, isOverlapPopover, mode, panePropsCss, popoverRest, restPaneProps, sxPaneProp'
  // )
  var popoverProps = useMemo(function () {
    return _objectSpread2({
      elevation: 'none',
      zIndex: isOverlapPopover ? 'selectOverlapPopover' : 'selectPopover',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      paneProps: _objectSpread2(_objectSpread2(_objectSpread2({
        display: 'flex',
        flexDirection: 'column',
        elevation: 'none',
        bg: 'white',
        borderRadius: isFlatDesign$1 ? 'default' : 'none',
        border: inputProps.state ? inputProps.state : isFlatDesign$1 ? 'focus' : 'primary',
        boxShadow: inputProps.state ? inputProps.state : isFlatDesign$1 ? 'dropShadow' : 'primary',
        fontSize: fontSize
      }, mode === 'select' ? {
        maxHeight: '400px'
      } : {}), {}, {
        sx: _objectSpread2(_objectSpread2({}, scrollbar.transition), sxPaneProp)
      }, panePropsCss), restPaneProps),
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      }
    }, popoverRest);
  }, [fontSize, inputProps.state, isFlatDesign$1, isOverlapPopover, mode, panePropsCss, popoverRest, restPaneProps, sxPaneProp]);

  // useWhatChanged([openPopup, inputSize], 'openPopup, inputSize')

  // refresh render when button size change, to adjust the adorner for the popover.
  useEffect(function () {
    if (openPopup) {
      // eslint-disable-next-line no-console
      // console.log(`----- autocomplete inputSize change -----`)
      setRefreshToken(function (prev) {
        return ++prev;
      });
    }
  }, [openPopup, inputSize]);
  useEffect(function () {
    if (!inView) {
      handleClose();
    }
  }, [handleClose, inView]);
  var inputRemainingProps = useMemo(function () {
    var _ref4;
    return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, mode === 'select' ?
    // by passing a different tag for the mode the theme will
    // resolve to the default input behavior and style of readonly
    {
      mode: "select".concat(readOnlyProps ? '-readonly' : ''),
      pr: hasClearIcon || !hasSelectedValues ? 'xl' : 0
    } : {}), mode !== 'select' && multiple ? hasSelectedValues ? {
      pl: 0,
      py: 0,
      minHeight: 'calc(2em - 8px)'
    } : {
      py: 0,
      minHeight: 'calc(2em - 8px)'
    } : {}), (hasClearIcon || hasPopupIcon) && {
      afterProps: afterProps,
      after: after
    }), defaultInputProps), utils.omit(inputProps, ['ref'])), {}, {
      sx: _objectSpread2(_objectSpread2({}, inputProps.sx), hasClearIcon && hasPopupIcon && !multiple ? (_ref4 = {}, _defineProperty(_ref4, "".concat(INPUT_HOVER_PSEUDO_FIELD, " > input"), {
        pr: 'xl+4'
      }), _defineProperty(_ref4, "".concat(INPUT_FOCUS_PSEUDO_FIELD, " > input"), {
        pr: 'xl+4'
      }), _ref4) : {})
    }, rest), mode === 'select' && inputProps.value !== undefined && inputProps.value !== null ? {
      value: inputProps.value
    } : {});
  }, [after, afterProps, hasClearIcon, hasPopupIcon, hasSelectedValues, inputProps, mode, multiple, readOnlyProps, rest]);
  var _openPopup = openPopup && inView && popoverContent.props.children.some(function (i) {
    return !!i;
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExtendedAutocompleteInput, _extends({
    ref: utils.mergeRefs(ref, inputProps.ref, inputRef, _inputRef, inViewRef),
    "aria-describedby": id,
    rootComponent: FlexInline,
    rootProps: rootProps,
    className: classNames([classes.input, mode === 'select' && 'autocomplete-select']),
    variant: variant,
    size: size,
    fontSize: fontSize,
    disabled: disabled,
    readOnly: readOnlyProps || mode === 'select',
    width: width,
    maxWidth: maxWidth,
    hidableTags: hidableTags,
    placeholder: hidePlaceholder && multiple && hasSelectedValues ? undefined : placeholder,
    tagsVariant: mode !== 'select' && multiple && tagProps.variant ? tagProps.variant : undefined,
    isLoading: loading,
    before: startAdornment
  }, inputRemainingProps, {
    hiderErrorIcon: true // temp prop
  })), /*#__PURE__*/React.createElement(Popover, _extends({
    id: id,
    open: _openPopup,
    anchorEl: anchorEl,
    onClose: handleClose,
    onEntering: handlePopupEntering,
    onExited: handleOnExited,
    onEntered: handleOnEntered,
    onReposition: handlePopoverReposition,
    disablePortal: disablePortal,
    hideBackdrop: true,
    disableAutoFocus: true,
    disableEnforceFocus: true,
    disableRestoreFocus: true,
    doNotCoverAnchorEl: mode !== 'select',
    adornerComponent: renderAdornerComponent,
    refreshToken: refreshToken
  }, popoverProps), popoverContent));
});
var MemoAutocomplete = /*#__PURE__*/React.memo(AutocompletePrivate);
function InternalAutocomplete(props) {
  return /*#__PURE__*/React.createElement(ThemeUIProvider, {
    theme: AutocompleteTheme
  }, /*#__PURE__*/React.createElement(MemoAutocomplete, props));
}

/**
 * Autocomplete is a component that allows users to select a value from a list
 * of options.
 *
 * It supports a variety of {@link AutocompleteProps.mode modes}
 * which configure the behavior of the component. By default, the component is
 * a text input where the user can type to filter a list of selectable options.
 *
 * ## Options
 *
 * Autocomplete supports options of any type, but has special behavior for
 * objects with a `label` and `value` property. The
 * {@link AutocompleteProps.value value} prop is one (or more, for
 * {@link AutocompleteProps.multiple multiple} selections) of these objects.
 *
 * You must ensure the following guarantees for {@link AutocompleteProps.options options} and
 * {@link AutocompleteProps.value values}:
 * 1. Each value is an entry in the options array. It cannot just be an object
 *    of the same shape, it _must_ pass a [reference equality
 *    check](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Equality_comparisons_and_sameness).
 *    That is, `options.includes(value) === true` must hold for all values.
 * 2. If you cannot provide the above guarantee, you _must_ provide a
 *    {@link AutocompleteProps.isOptionEqualToValue isOptionEqualToValue}
 *    function that provides the same guarantee.
 *
 * ## [Controlled vs Uncontrolled](https://react.dev/learn/sharing-state-between-components#controlled-and-uncontrolled-components) Mode
 * When using the component in a controlled mode you must initialize the component via {@link AutocompleteProps.value value}
 * props. Use `null` to initialize it with nothing selected. In an uncontrolled mode do not pass any value via
 * {@link AutocompleteProps.value value} and use {@link AutocompleteProps.defaultValue defaultValue} props to initialize your initial state
 *
 * ## Free Solo Mode
 * @see [Storybook](https://storybook.costar.com/theme-ui/?path=/story/packages-theme-ui-autocomplete--07-free-solo-dialog)
 *
 * @template T The type of the options. If your options are objects, you may
 * find {@link SelectOption} useful.
 * @template Multiple If `true`, the component will allow multiple selections.
 * @template DisableClearable If `true`, the component will not allow the user
 * to clear the selection. It also removes the clear button on the input.
 * Defaults to `false`.
 * @template FreeSolo Enables free solo mode, where user input is not bound by
 * a static list of options. Defaults to `false`.
 *
 * @see {@link SelectOption} for a base utility type for typing `{ label, value }` options.
 * @see [Storybook](https://storybook.costar.com/theme-ui/?path=/story/packages-theme-ui-autocomplete)
 */
function Autocomplete(props) {
  return /*#__PURE__*/React.createElement(ThemeUIProvider, {
    theme: AutocompleteTheme
  }, /*#__PURE__*/React.createElement(MemoAutocomplete, props));
}
if (utils.__DEV__) {
  Autocomplete.displayName = 'Autocomplete';
}
Autocomplete.Formik = formikField(Autocomplete, {
  hasFieldWrapper: true,
  isAutoComplete: true
});

export default Autocomplete;
export { InternalAutocomplete };
//# sourceMappingURL=autocomplete.esm.js.map
